import type { PublicNotification } from "@dropl/shared";
import {
  getState,
  kindIcon,
  markAllRead,
  markRead,
  refresh,
  remove as removeNotification,
  subscribe,
} from "../state/notifications.js";
import { navigate } from "../router/index.js";
import { t } from "../i18n/index.js";
import { escapeHtml } from "./shell.js";

export function renderBell(): string {
  const s = getState();
  return `
    <div class="bell" data-bell>
      <button type="button" class="theme-toggle bell__btn" data-bell-btn
              aria-label="${escapeHtml(t("notifications.bell.aria"))}"
              aria-haspopup="true" aria-expanded="false">
        <i class="fa-solid fa-bell" aria-hidden="true"></i>
        ${s.unread > 0 ? `<span class="bell__badge" data-bell-badge>${formatCount(s.unread)}</span>` : ""}
      </button>
      <div class="bell__panel" data-bell-panel hidden role="dialog"
           aria-label="${escapeHtml(t("notifications.bell.aria"))}">
        <div class="bell__panel-head">
          <strong>${escapeHtml(t("notifications.title"))}</strong>
          <button type="button" class="btn btn--ghost btn--sm" data-bell-mark-all>
            <i class="fa-solid fa-check-double" aria-hidden="true"></i>
            ${escapeHtml(t("notifications.mark_all_read"))}
          </button>
        </div>
        <div class="bell__list" data-bell-list></div>
      </div>
    </div>`;
}

export function wireBell(): void {
  const root = document.querySelector<HTMLElement>("[data-bell]");
  if (!root) return;
  const btn = root.querySelector<HTMLButtonElement>("[data-bell-btn]");
  const panel = root.querySelector<HTMLElement>("[data-bell-panel]");
  const list = root.querySelector<HTMLElement>("[data-bell-list]");
  const markAllBtn = root.querySelector<HTMLButtonElement>("[data-bell-mark-all]");
  if (!btn || !panel || !list || !markAllBtn) return;

  const setOpen = (open: boolean): void => {
    panel.hidden = !open;
    btn.setAttribute("aria-expanded", String(open));
    root.classList.toggle("bell--open", open);
  };

  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    const willOpen = panel.hidden;
    setOpen(willOpen);
    if (willOpen) void refresh();
  });

  document.addEventListener("click", (e) => {
    if (!panel.hidden && !root.contains(e.target as Node)) setOpen(false);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !panel.hidden) setOpen(false);
  });

  markAllBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    void markAllRead();
  });

  let page = 0;
  let lastItems: PublicNotification[] = [];

  const renderPage = (): void => {
    const totalPages = Math.max(1, Math.ceil(lastItems.length / PAGE_SIZE));
    if (page >= totalPages) page = totalPages - 1;
    if (page < 0) page = 0;
    list.innerHTML = renderList(lastItems, page, totalPages);
  };

  list.addEventListener("click", (e) => {
    const tgt = e.target as HTMLElement | null;

    const pager = tgt?.closest<HTMLElement>("[data-bell-page]");
    if (pager) {
      e.stopPropagation();
      const delta = Number(pager.dataset.bellPage ?? "0");
      page += delta;
      renderPage();
      return;
    }

    const delBtn = tgt?.closest<HTMLElement>("[data-notif-delete]");
    if (delBtn) {
      e.stopPropagation();
      const id = delBtn.dataset.notifDelete ?? "";
      void removeNotification(id);
      return;
    }
    const readBtn = tgt?.closest<HTMLElement>("[data-notif-read]");
    if (readBtn) {
      e.stopPropagation();
      const id = readBtn.dataset.notifRead ?? "";
      void markRead(id);
      return;
    }
    const row = tgt?.closest<HTMLElement>("[data-notif-id]");
    if (!row) return;
    const id = row.dataset.notifId ?? "";
    const url = row.dataset.notifUrl ?? "";
    void markRead(id);
    if (url) {
      setOpen(false);
      if (url.startsWith("/") && !url.startsWith("//")) navigate(url);
      else window.open(url, "_blank", "noopener");
    }
  });

  subscribe((s) => {

    const badge = btn.querySelector<HTMLElement>("[data-bell-badge]");
    if (s.unread > 0) {
      if (badge) badge.textContent = formatCount(s.unread);
      else btn.insertAdjacentHTML("beforeend",
        `<span class="bell__badge" data-bell-badge>${formatCount(s.unread)}</span>`);
    } else if (badge) {
      badge.remove();
    }

    const firstChanged = lastItems[0]?.id !== s.items[0]?.id;
    lastItems = s.items;
    if (firstChanged) page = 0;
    renderPage();
  });
}

const PAGE_SIZE = 4;

function renderList(items: PublicNotification[], page: number, totalPages: number): string {
  if (items.length === 0) {
    return `<p class="bell__empty t-small">${escapeHtml(t("notifications.empty"))}</p>`;
  }
  const start = page * PAGE_SIZE;
  const slice = items.slice(start, start + PAGE_SIZE);
  const rows = slice.map((n) => `
    <div class="bell__item ${n.readAt ? "" : "bell__item--unread"}"
         data-notif-id="${escapeHtml(n.id)}"
         ${n.url ? `data-notif-url="${escapeHtml(n.url)}"` : ""}
         role="button" tabindex="0">
      <span class="bell__item-icon bell__item-icon--${escapeHtml(n.kind)}">
        <i class="fa-solid ${escapeHtml(n.icon ?? kindIcon(n.kind))}" aria-hidden="true"></i>
      </span>
      <div class="bell__item-body">
        <div class="bell__item-title">
          ${escapeHtml(n.title)}
          ${!n.readAt ? '<span class="bell__item-dot" aria-hidden="true"></span>' : ""}
        </div>
        ${n.body ? `<p class="bell__item-text t-small">${escapeHtml(n.body)}</p>` : ""}
        <p class="bell__item-time t-micro">${escapeHtml(formatTime(n.createdAt))}</p>
      </div>
      <div class="bell__item-actions">
        <button type="button"
                class="bell__item-check ${n.readAt ? "bell__item-check--read" : ""}"
                ${n.readAt ? "disabled" : `data-notif-read="${escapeHtml(n.id)}"`}
                aria-label="${escapeHtml(t(n.readAt ? "notifications.read" : "notifications.mark_read"))}"
                title="${escapeHtml(t(n.readAt ? "notifications.read" : "notifications.mark_read"))}">
          <i class="fa-solid ${n.readAt ? "fa-circle-check" : "fa-circle"}" aria-hidden="true"></i>
        </button>
        <button type="button" class="bell__item-delete"
                data-notif-delete="${escapeHtml(n.id)}"
                aria-label="${escapeHtml(t("notifications.delete"))}"
                title="${escapeHtml(t("notifications.delete"))}">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </div>
    </div>`).join("");

  const showPrev = page > 0;
  const showNext = page < totalPages - 1;
  const pager = totalPages > 1 ? `
    <div class="bell__pager">
      ${showPrev ? `
        <button type="button" class="btn btn--ghost btn--sm" data-bell-page="-1"
                aria-label="${escapeHtml(t("notifications.prev"))}">
          <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
        </button>` : `<span class="bell__pager-spacer" aria-hidden="true"></span>`}
      <span class="t-micro bell__pager-info">${page + 1} / ${totalPages}</span>
      ${showNext ? `
        <button type="button" class="btn btn--ghost btn--sm" data-bell-page="1"
                aria-label="${escapeHtml(t("notifications.next"))}">
          <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
        </button>` : `<span class="bell__pager-spacer" aria-hidden="true"></span>`}
    </div>` : "";

  return rows + pager;
}

function formatCount(n: number): string {
  return n > 99 ? "99+" : String(n);
}

import { formatRelativeDate } from "./format.js";
const formatTime = formatRelativeDate;
